import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Container, Table, Alert, Button, Spinner, Modal, Form } from "react-bootstrap";

const OrderHistory = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [reviewData, setReviewData] = useState({
        rating: 5,
        review: '',
        location: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem("token");
            if (!token) {
                navigate("/login");
                return;
            }

            const response = await axios.get("https://madhusewingmachines.com/api/orders/my-orders", {
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });

           
            if (response.data.success) {
                setOrders(response.data.data || []);
            } else {
                throw new Error(response.data.message || "Failed to fetch orders");
            }
        } catch (error) {
            let errorMessage = "Failed to load orders";

            if (error.response) {
                if (error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/login");
                    return;
                }
                errorMessage = error.response.data?.message || errorMessage;
            } else {
                errorMessage = error.message || errorMessage;
            }

            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
    }, []);

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleReviewClick = (product, order) => {
        console.log('Selected product for review:', product);
        console.log('Order:', order);
        setSelectedProduct(product);
        setSelectedOrder(order);
        setShowReviewModal(true);
        setReviewData({
            rating: 5,
            review: '',
            location: ''
        });
    };

    const handleReviewSubmit = async (e) => {
        e.preventDefault();
        
        if (!reviewData.review.trim()) {
            alert('Please write a review');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem("token");
            const user = JSON.parse(localStorage.getItem("user") || '{}');

            console.log('Submitting review:', {
                name: user.name || 'Anonymous',
                location: reviewData.location || 'India',
                review: reviewData.review,
                rating: reviewData.rating,
                productId: selectedProduct.productId,
                orderId: selectedOrder._id,
                verified: true
            });

            const response = await axios.post(
                "https://madhusewingmachines.com/api/product-reviews",
                {
                    name: user.name || 'Anonymous',
                    location: reviewData.location || 'India',
                    review: reviewData.review,
                    rating: reviewData.rating,
                    productId: selectedProduct.productId,
                    orderId: selectedOrder._id,
                    userId: user._id || null,
                    verified: true
                },
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    },
                }
            );

            console.log('Review response:', response.data);
            alert('Review submitted successfully! Thank you for your feedback.');
            setShowReviewModal(false);
            setSelectedProduct(null);
            setSelectedOrder(null);
        } catch (error) {
            console.error('Error submitting review:', error);
            console.error('Error response:', error.response?.data);
            alert(error.response?.data?.message || 'Failed to submit review. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Container className="text-center" style={{ marginTop: '120px' }}>
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p>Loading your orders...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container style={{ marginTop: '120px' }}>
                <Alert variant="danger">
                    {error}
                    <Button variant="primary" className="ms-3" onClick={() => navigate("/login")}>
                        Go to Login
                    </Button>
                </Alert>
            </Container>
        );
    }

    if (orders.length === 0) {
        return (
            <Container className="text-center" style={{ marginTop: '120px' }}>
                <Alert variant="info">
                    <h5>No orders found.</h5>
                    <Button variant="success" className="mt-3" onClick={() => navigate("/")}>
                        Start Shopping
                    </Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container style={{ marginTop: '120px', marginBottom: '40px' }}>
            <h2 className="mb-4">Your Order History</h2>
            <div className="table-responsive">
                <Table striped bordered hover>
                    <thead>
                        <tr>   
                            <th>Order #</th>
                            <th>Date</th>
                            <th>Items</th>
                            <th>Status</th>
                            <th>Total</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.map((order) => (
                            <tr key={order._id}>
                                <td>{order.orderNumber ?? order._id.substring(order._id.length - 6).toUpperCase()}</td>
                                <td>{formatDate(order.createdAt)}</td>
                                <td>
                                    {order?.cartItems?.map((item, index) => (
                                        <div key={index} className="mb-2">
                                            <div>{item.name} (x{item.quantity})</div>
                                            {item.type === 'embroidery-design' && item.productId && (
                                                <Button 
                                                    size="sm" 
                                                    variant="success"
                                                    onClick={() => window.open(`https://madhusewingmachines.com/api/embroidery-designs/${item.productId}/download`, '_blank')}
                                                    className="mt-1 me-2"
                                                >
                                                    📥 Download Design
                                                </Button>
                                            )}
                                            {order.status === 'Delivered' && (
                                                <Button 
                                                    size="sm" 
                                                    variant="outline-primary"
                                                    onClick={() => handleReviewClick(item, order)}
                                                    className="mt-1"
                                                >
                                                    ⭐ Write Review
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                </td>
                                <td>
                                    <span
                                        className={`badge ${order.status === "Delivered"
                                                ? "bg-success"
                                                : order.status === "Cancelled"
                                                    ? "bg-danger"
                                                    : "bg-warning"
                                            }`}
                                    >
                                        {order.status}
                                    </span>
                                </td>
                                <td>₹{order?.total?.toFixed(2)}</td>
                                <td>
                                    <Button 
                                        size="sm" 
                                        variant="info"
                                        onClick={() => navigate(`/orders/${order._id}`)}
                                    >
                                        View Details
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {/* Review Modal */}
            <Modal show={showReviewModal} onHide={() => setShowReviewModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Write a Review</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedProduct && (
                        <div className="mb-3">
                            <h6>Product: {selectedProduct.name}</h6>
                            <p className="text-muted">Share your experience with this product</p>
                        </div>
                    )}
                    <Form onSubmit={handleReviewSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>Rating</Form.Label>
                            <div className="d-flex gap-2">
                                {[1, 2, 3, 4, 5].map((star) => (
                                    <span
                                        key={star}
                                        onClick={() => setReviewData({...reviewData, rating: star})}
                                        style={{
                                            fontSize: '32px',
                                            cursor: 'pointer',
                                            color: star <= reviewData.rating ? '#ffc107' : '#e0e0e0'
                                        }}
                                    >
                                        ★
                                    </span>
                                ))}
                            </div>
                            <Form.Text className="text-muted">
                                {reviewData.rating} out of 5 stars
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Your Location (Optional)</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="e.g., Mumbai, India"
                                value={reviewData.location}
                                onChange={(e) => setReviewData({...reviewData, location: e.target.value})}
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Your Review *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Tell us about your experience with this product..."
                                value={reviewData.review}
                                onChange={(e) => setReviewData({...reviewData, review: e.target.value})}
                                required
                            />
                        </Form.Group>

                        <div className="d-flex gap-2 justify-content-end">
                            <Button variant="secondary" onClick={() => setShowReviewModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner size="sm" animation="border" className="me-2" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Submit Review'
                                )}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    );
};
 
export default OrderHistory;